import React, { useEffect, useRef, useState } from 'react';
import type { AuthState, SessionResponse } from '@lumos/contracts';
import { ApiError, describeError } from '../api/client.js';
import { announceDevice, getAuthState, login } from '../api/endpoints.js';
import { PendingPage } from './PendingPage.js';
import { AuthScene } from '../components/AuthScene.js';
import { LockIcon, UserIcon } from '../components/icons.js';

const BLOCKED_TEXT: Record<NonNullable<AuthState['signInBlocked']>, string> = {
  'no-device': 'портал это устройство ещё не знает',
  'unknown-device': 'браузер прислал запись устройства, которой на сервере нет',
  pending: 'устройство ждёт одобрения',
  blocked: 'устройство отклонено администратором',
  revoked: 'доступ устройства отозван',
  'no-owner': 'устройство одобрено, но ему не назначен владелец',
  'owner-unavailable': 'владелец устройства удалён или отключён',
  'password-required': 'устройство одобрено; это обычный браузер, ему нужен логин и пароль',
};

/** Строка о состоянии устройства под формой входа. */
export function deviceDiagnostic(auth: AuthState): string {
  const reason = auth.signInBlocked ?? null;
  const parts = [reason === null ? 'причина не названа' : BLOCKED_TEXT[reason]];
  if (auth.device) {
    parts.push(`устройство …${auth.device.id.slice(-6)}`);
    if (auth.device.passwordless) parts.push('без пароля');
  }
  if (auth.deviceWaiting === true) parts.push('администратор ждёт устройство');
  return `Состояние: ${parts.join(' · ')}`;
}

/** Как часто страница входа спрашивает, не ждёт ли администратор устройство. */
const WAITING_POLL_MS = 5000;

export function LoginPage({ auth, onLogin }: { auth: AuthState; onLogin: (state: AuthState) => void }): React.JSX.Element {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  // На экране ожидания выбрали «войти с логином и паролем» — показываем форму.
  const [usePassword, setUsePassword] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault(); setBusy(true); setError(null);
    try {
      const session: SessionResponse = await login({ username, password, device: { platform: 'web', label: navigator.userAgent.slice(0, 80), model: navigator.platform } });
      onLogin({ ...auth, authenticated: true, user: session.user, device: session.device, csrfToken: session.csrfToken });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'DEVICE_PENDING') {
        const code = e.details?.code;
        if (typeof code === 'string') {
          // Пароль введён, устройство ждёт одобрения — снова экран ожидания.
          setUsePassword(false);
          setPendingCode(code);
        } else setError(e.message);
      } else setError(describeError(e));
    } finally { setBusy(false); }
  }

  // Режим ожидания (решение владельца 06.10.2026): администратор включил его
  // и ждёт новое устройство. Страница входа объявляет своё устройство серверу
  // — логин и пароль с пульта набирать не нужно — и показывает код.
  //
  // Сама отзывается страница, которую портал ещё не знает, — и только что
  // открытая, и стоявшая открытой: телевизор обычно так и стоит на форме
  // входа, когда администратор включает режим. Устройству, которое портал уже
  // знает (вышли, сессия кончилась), новая запись подменила бы прежнюю,
  // поэтому ему — кнопка «Подключить это устройство», а не самовольный отзыв.
  const [seen, setSeen] = useState(auth);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  function join(trigger: 'open' | 'manual'): void {
    setJoining(true);
    setJoinError(null);
    announceDevice(trigger)
      .then((enrolled) => {
        if (enrolled.status === 'pending') setPendingCode(enrolled.code);
      })
      // Молчать нельзя: отказ, которого не видно, выглядит как «не работает».
      .catch((e) => setJoinError(describeError(e)))
      .finally(() => setJoining(false));
  }
  const waitingOn = seen.deviceWaiting === true;
  const unknownBrowser = seen.signInBlocked === 'no-device' || seen.signInBlocked === 'unknown-device';
  // Одна попытка на одно включение режима: отказ не повторяем каждые 5 секунд.
  const answered = useRef(false);
  useEffect(() => {
    if (!waitingOn) {
      answered.current = false;
      setJoinError(null);
      return;
    }
    if (!unknownBrowser || answered.current || pendingCode !== null || usePassword) return;
    answered.current = true;
    join('open');
  }, [waitingOn, unknownBrowser, pendingCode, usePassword]);
  // Устройство уже ждёт одобрения (страницу перезагрузили, телевизор уснул и
  // проснулся): сразу показываем код, а не форму входа. Один раз за жизнь
  // страницы; код своему ждущему устройству сервер отдаёт и без режима.
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    if (auth.device?.status === 'pending') join('open');
  }, []);
  // Режим могут включить и после того, как страницу открыли: состояние
  // переспрашиваем, пока форма на экране; запрос один и лёгкий.
  useEffect(() => {
    if (pendingCode !== null) return undefined;
    const timer = window.setInterval(() => {
      getAuthState()
        .then((state) => {
          // Сервер говорит, что устройство уже впущено (одобрили, пока форма
          // была на экране) — входим, что бы к этому ни привело.
          if (state.authenticated) {
            window.location.reload();
            return;
          }
          setSeen(state);
        })
        .catch(() => undefined);
    }, WAITING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [pendingCode]);
  // Кнопка — знакомому устройству и тому, чей отзыв сервер не принял.
  const offer = waitingOn && !usePassword && seen.device?.status !== 'pending' && (!unknownBrowser || joinError !== null);

  if (pendingCode && !usePassword) {
    return (
      <PendingPage
        code={pendingCode}
        onApproved={() => window.location.reload()}
        onUsePassword={() => {
          setUsePassword(true);
          setPendingCode(null);
        }}
      />
    );
  }
  return (
    <AuthScene>
      <h1>Авторизация</h1>
      <form onSubmit={submit}>
        {/* Подпись — внутри поля и в `aria-label`: значок и подсказка говорят
            то же самое, а строка над каждым полем удлиняла форму вдвое. */}
        <label className="auth-field">
          <UserIcon className="auth-field-icon" />
          <input
            required
            aria-label="Логин"
            placeholder="Логин"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
          />
        </label>
        <label className="auth-field">
          <LockIcon className="auth-field-icon" />
          <input
            required
            type="password"
            aria-label="Пароль"
            placeholder="Пароль"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        {error && <p className="state error">{error}</p>}
        <button className="primary-action" disabled={busy}>{busy ? 'Проверка…' : 'Войти'}</button>
      </form>
      {/* Почему форма вообще на экране: что сервер знает об этом устройстве.
          Мелко и по делу — это читают, когда «одобрил, а оно просит пароль». */}
      {offer && (
        <div className="auth-waiting-offer" role="status">
          <p>Администратор ждёт новое устройство.</p>
          {joinError && <p className="state error">Не удалось подключить: {joinError}</p>}
          <button type="button" className="primary-action" disabled={joining} onClick={() => join('manual')}>
            Подключить это устройство
          </button>
        </div>
      )}
      <p className="auth-diagnostic">{deviceDiagnostic(seen)}</p>
    </AuthScene>
  );
}
